import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { debounceTime, filter, map, switchMap, take, timeout, catchError } from 'rxjs/operators';
import { NativeStorageService, UserService } from 'army-builder-shared';
import { from, of } from 'rxjs';
import { RedirectService } from './redirect.service';

@Injectable({
    providedIn: 'root'
})
export class UserGuard {
    private requiredRoles: string[] = [];

    constructor(
        private router: Router,
        private userService: UserService,
        private nativeStorageService: NativeStorageService,
        private redirectService: RedirectService
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        console.log('UserGuard: canActivate called for', state.url);
        return from(this.nativeStorageService.checkServiceInit()).pipe(
            switchMap(() => {
                console.log('UserGuard: storage init complete, checking login state');
                return this.userService.login$;
            }),
            debounceTime(100), // Give the app a moment to initialize
            take(1), // Take the first emission after debounce
            timeout(2000), // Shorter timeout since we're not waiting for checked flag
            catchError((error) => {
                console.error('UserGuard: Error or timeout waiting for login state', error);
                // On error/timeout, assume not logged in and redirect to home
                this.redirectService.setRedirectUrl(state.url);
                this.router.navigateByUrl('/');
                return of({ checked: true, user: null, error: null, loading: false });
            }),
            map((l) => {
                console.log('UserGuard: evaluating access for', state.url, {
                    checked: l.checked,
                    user: l.user?.email,
                    roles: l.user?.roles
                });

                // If no user, deny access
                if (!l.user) {
                    console.log('UserGuard: Navigating back to home because user is not logged in');
                    // Store the intended URL for redirect after login
                    this.redirectService.setRedirectUrl(state.url);
                    this.router.navigateByUrl('/');
                    return false;
                }

                if (!userHasAccess(l.user)) {
                    console.log("UserGuard: Navigating back to home because user doesn't have access", l.user);
                    // Store the intended URL for redirect after login
                    this.redirectService.setRedirectUrl(state.url);
                    this.router.navigateByUrl('/');
                    return false;
                }

                const requiredRoles = route.data.requiredRoles;
                if (requiredRoles) {
                    // Check if user has any of the required roles
                    const hasRequiredRole = requiredRoles.some((role) => l.user.roles.includes(role));
                    if (!hasRequiredRole) {
                        // User does not have any of the required roles, redirect to unauthorized page
                        console.log("UserGuard: Navigating back to home because user doesn't have required roles", l.user);
                        // Store the intended URL for redirect after login
                        this.redirectService.setRedirectUrl(state.url);
                        this.router.navigateByUrl('/');
                        return false;
                    }
                }

                console.log('UserGuard: Access granted for', state.url);
                return true;
            })
        );
    }
}

export function userHasAccess(u) {
    return u?.roles?.includes('app_admin') || u?.roles?.includes('app_user');
}
